import { useSiteData } from '../context/DataContext.jsx'
import RailBlock from '../components/RailBlock.jsx'
import ErrorFallback from '../components/ErrorFallback.jsx'
import Skeleton from '../components/Skeleton.jsx'

function formatDateRange(start, end) {
  const startYear = new Date(start).getFullYear()
  const endYear = end ? new Date(end).getFullYear() : 'present'
  return `${startYear}–${endYear}`
}

function ExperiencePage() {
  const { data: siteData, loading, error, refetch } = useSiteData()
  const data = siteData?.experience || []

  if (loading) {
    return (
      <div className="pg-layout">
        <div>
          <Skeleton className="h-4 w-28 mb-3" />
          <Skeleton className="h-14 w-24 mb-10" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="border-t border-soft-rule py-7">
              <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_200px] gap-4 sm:gap-8">
                <Skeleton className="h-4 w-20" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-5/6" />
                </div>
                <Skeleton className="h-3 w-24 justify-self-end" />
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    )
  }

  if (error) return <ErrorFallback message="Failed to load experience" onRetry={refetch} />

  const experiences = data || []

  return (
    <div className="pg-layout">
      {/* LEFT */}
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.6px] text-soft-faint mb-3">
          Work · three roles
        </p>
        <h1 className="text-[36px] sm:text-[48px] md:text-[56px] font-normal leading-[1.02] tracking-[-1.8px] mb-10">
          Work
        </h1>

        <div>
          {experiences.map((exp, index) => (
            <div
              key={exp.id}
              className={`grid grid-cols-[120px_1fr_200px] gap-8 py-7 ${
                index > 0 ? 'border-t border-soft-rule' : ''
              }`}
            >
              <span className="font-mono text-[12px] text-soft-faint">
                {formatDateRange(exp.start_date, exp.end_date)}
              </span>
              <div>
                <h2 className="text-[20px] font-medium leading-[1.2] tracking-[-0.3px] text-soft-ink mb-1">
                  {exp.title}
                </h2>
                <p className="text-[15px] leading-[1.55] text-soft-dim mb-3">{exp.company}</p>
                {exp.formatted_bullets?.length > 0 && (
                  <ul className="space-y-1.5">
                    {exp.formatted_bullets.slice(0, 3).map((bullet) => (
                      <li key={bullet} className="text-[15px] leading-[1.55] text-soft-dim">
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="text-right">
                <p className="font-mono text-[12px] text-soft-faint mb-1">{exp.location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT rail */}
      <aside>
        <RailBlock eyebrow="Tenure summary" top>
          <p className="text-[13.5px] text-soft-ink mb-1">3 years across 3 roles</p>
          <p className="font-mono text-[11px] text-soft-faint">2022 - present</p>
        </RailBlock>

        <RailBlock eyebrow="Touched these systems">
          <div className="flex flex-wrap gap-2">
            {['Java', 'Kotlin', 'Python', 'Spring Boot', 'ReactJS', 'ExtJS', 'PostgreSQL', 'AWS', 'Docker', 'Jenkins'].map((tech) => (
              <span
                key={tech}
                className="font-mono text-[11px] uppercase tracking-[0.5px] text-soft-dim px-2 py-1 border border-soft-rule"
              >
                {tech}
              </span>
            ))}
          </div>
        </RailBlock>
      </aside>
    </div>
  )
}

export default ExperiencePage
